'use client';

import { useQuery } from '@tanstack/react-query';
import {
  fetchContentModerationFindingDetail,
  fetchContentModerationFindingsPage,
  provenanceKeys,
  type ContentModerationFindingDetail,
  type ContentModerationFindingsPage,
  type DetectionType,
} from '@/shared/cors-provenance-api';
import { useProvenanceSession } from './useProvenanceSession';
import { useContentModerationCase } from './useContentModerationFolderName';

const MAX_FINDING_SCAN_PAGES = 8;
const SUMMARY_PAGE_LIMIT = 200;

/**
 * Image description for one file. An md5 goes straight to the detail endpoint.
 * A path scans summary pages for the md5, then loads that finding's detail.
 */
export async function resolveFindingImageDescription(args: {
  fileMd5?: string | null;
  filePath?: string | null;
  fetchPage(nextToken: string | null): Promise<ContentModerationFindingsPage>;
  fetchDetail(fileMd5: string): Promise<Pick<ContentModerationFindingDetail, 'imageDescription'>>;
}): Promise<string | null> {
  const directMd5 = args.fileMd5?.trim();
  if (directMd5) {
    const detail = await args.fetchDetail(directMd5);
    return detail.imageDescription?.trim() || null;
  }
  const path = args.filePath?.trim();
  if (!path) return null;
  let nextToken: string | null = null;
  for (let page = 0; page < MAX_FINDING_SCAN_PAGES; page += 1) {
    const result = await args.fetchPage(nextToken);
    const match = result.items.find((finding) => pathsMatchForFinding(finding.filePath, path));
    if (match) {
      const detail = await args.fetchDetail(match.fileMd5);
      return detail.imageDescription?.trim() || null;
    }
    nextToken = result.nextToken;
    if (!nextToken) break;
  }
  return null;
}

export function toDetectionType(value: string | null | undefined): DetectionType | undefined {
  if (value === 'CSAM' || value === 'NSFW') return value;
  return undefined;
}

/** Exact path or a full-segment suffix — never a bare filename match. */
export function pathsMatchForFinding(findingPath: string, lookupPath: string): boolean {
  const a = findingPath.trim();
  const b = lookupPath.trim();
  if (!a || !b) return false;
  if (a === b) return true;
  if (!a.includes('/') || !b.includes('/')) return false;
  const [longer, shorter] = a.length >= b.length ? [a, b] : [b, a];
  if (!longer.endsWith(shorter)) return false;
  const boundary = longer.length - shorter.length - 1;
  return boundary >= 0 && longer[boundary] === '/';
}

/**
 * Resolve the LLM image description for a content-moderated file.
 * An md5 reads the detail endpoint. A path scans summary pages for that md5
 * first. Used when file-info omits ``contentModeration.imageDescription``.
 */
export function useContentModerationImageDescription(
  caseId: string | null | undefined,
  opts: {
    detectionType?: DetectionType;
    fileMd5?: string | null;
    filePath?: string | null;
    enabled?: boolean;
  } = {},
) {
  const { detectionType, fileMd5, filePath, enabled = true } = opts;
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  const hasIdentity = Boolean(fileMd5?.trim() || filePath?.trim());
  // Wire ``case_id`` is the folder segment, not the nav display name.
  const { folderName, isResolving: isCaseResolving } = useContentModerationCase(caseId);

  const query = useQuery({
    queryKey: provenanceKeys.contentModerationImageDescription(
      cellId,
      folderName,
      detectionType ?? null,
      fileMd5 ?? null,
      filePath ?? null,
    ),
    enabled:
      sessionEnabled && Boolean(folderName) && Boolean(detectionType) && enabled && hasIdentity,
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      resolveFindingImageDescription({
        fileMd5,
        filePath,
        fetchPage: (nextToken) =>
          fetchContentModerationFindingsPage({
            cellId: cellId as string,
            accessToken: accessToken as string,
            caseId: folderName as string,
            detectionType: detectionType as DetectionType,
            limit: SUMMARY_PAGE_LIMIT,
            nextToken,
            signal,
          }),
        fetchDetail: (md5) =>
          fetchContentModerationFindingDetail({
            cellId: cellId as string,
            accessToken: accessToken as string,
            caseId: folderName as string,
            detectionType: detectionType as DetectionType,
            fileMd5: md5,
            signal,
          }),
      }),
  });

  // Disabled is NOT pending here: a disabled TanStack query reports
  // ``isPending === true`` forever, so a consumer deriving a spinner from it
  // never settles when the case cannot resolve (cases query errored, WAF-blocked,
  // or case absent from the permissioned list). Callers should spin on
  // ``isCaseResolving || isFetching``, not on ``isPending``.
  return Object.assign(query, { isCaseResolving });
}
